import { useState } from 'react';
import { X, Copy, ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PaymentLinkModal — reusable Full / 50% / Custom picker that calls any tenant
 * "generate payment link" endpoint and displays the resulting PayMongo checkout
 * URL with Copy / Open buttons.
 *
 * Props:
 *   open (bool)
 *   onClose ()
 *   title (str)                — e.g. "Generate Payment Link — Team Warriors"
 *   subtitle (str)             — e.g. "Entry fee ₱1,200"
 *   baseAmount (number|null)   — the full amount owed (for showing 50% amount)
 *   allowDeposit50 (bool)      — default true
 *   allowCustom (bool)         — default true
 *   defaultChoice (str)        — 'full' | 'deposit50' | 'custom'
 *   onGenerate ({ amount_choice, amount_php }) → Promise<{ checkout_url }>
 */
export default function PaymentLinkModal({
  open, onClose, title, subtitle, baseAmount, onGenerate,
  allowDeposit50 = true, allowCustom = true, defaultChoice = 'full',
}) {
  const [choice, setChoice] = useState(defaultChoice);
  const [customAmt, setCustomAmt] = useState('');
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  if (!open) return null;
  const has = baseAmount && Number(baseAmount) > 0;

  const submit = async () => {
    if (choice === 'custom') {
      const n = parseFloat(customAmt);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error('Enter a custom amount above ₱0'); return;
      }
    }
    setBusy(true);
    try {
      const r = await onGenerate({
        amount_choice: choice,
        amount_php: choice === 'custom' ? parseFloat(customAmt) : undefined,
      });
      setLink(r.checkout_url);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    if (!link) return;
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Link copied — send it to the customer'))
      .catch(() => toast.error('Copy failed'));
  };

  const opt = (val, main, sub) => (
    <button
      key={val}
      onClick={() => setChoice(val)}
      data-testid={`paylink-choice-${val}`}
      style={{
        flex: 1, minWidth: 120,
        background: choice === val ? '#E6394614' : '#0A0A0A',
        border: `1px solid ${choice === val ? '#E63946' : '#2A2A2A'}`,
        color: choice === val ? '#E63946' : '#a3a3a3',
        borderRadius: 12, padding: '14px 12px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center',
      }}>
      <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}>{main}</div>
      {sub && <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{sub}</div>}
    </button>
  );

  return (
    <div data-testid="paylink-modal-backdrop" onClick={onClose}
         style={{ position: 'fixed', inset: 0, background: '#000A', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="paylink-modal"
           style={{ background: '#141414', border: '1px solid #232323', borderRadius: 18, padding: 24, maxWidth: 480, width: '100%', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          <div>
            <p style={{ fontFamily: 'Oswald, sans-serif', fontSize: 18, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>{title || 'Generate Payment Link'}</p>
            {subtitle && <p style={{ fontSize: 12, color: '#737373' }}>{subtitle}</p>}
          </div>
          <button onClick={onClose} data-testid="paylink-close-btn"
                  style={{ background: 'transparent', border: 'none', color: '#737373', cursor: 'pointer' }}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {!link && (
          <>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              {opt('full', 'Full amount', has ? `₱${Number(baseAmount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}` : null)}
              {allowDeposit50 && has && opt('deposit50', '50% deposit', `₱${(Number(baseAmount) * 0.5).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`)}
              {allowCustom && opt('custom', 'Custom', 'Enter amount')}
            </div>

            {choice === 'custom' && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, letterSpacing: 1, color: '#737373', textTransform: 'uppercase', fontWeight: 700, display: 'block', marginBottom: 6 }}>Amount (₱)</label>
                <input
                  data-testid="paylink-custom-amount"
                  type="number" min="1" step="1" value={customAmt}
                  onChange={(e) => setCustomAmt(e.target.value)}
                  placeholder="e.g. 500"
                  style={{ width: '100%', background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 10, padding: '10px 14px', outline: 'none', fontSize: 15, fontWeight: 700 }}
                />
              </div>
            )}

            <button
              data-testid="paylink-generate-btn"
              onClick={submit} disabled={busy}
              style={{ width: '100%', background: '#E63946', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 18px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'Oswald, sans-serif', fontSize: 13, opacity: busy ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? 'Generating…' : 'Generate PayMongo Link'}
            </button>
          </>
        )}

        {link && (
          <>
            <div style={{ background: '#00E67608', border: '1px solid #00E67640', borderRadius: 12, padding: 14, marginBottom: 12 }}>
              <p style={{ fontSize: 11, color: '#00E676', fontWeight: 800, letterSpacing: 1, marginBottom: 6 }}>PAYMENT LINK READY</p>
              <p data-testid="paylink-result-url" style={{ fontSize: 12, color: '#fff', wordBreak: 'break-all', fontFamily: 'JetBrains Mono, monospace' }}>{link}</p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button data-testid="paylink-copy-btn" onClick={copyLink}
                style={{ flex: 1, background: '#1A1A1A', color: '#a3a3a3', border: '1px solid #2A2A2A', borderRadius: 12, padding: '13px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Copy className="h-4 w-4" /> Copy link
              </button>
              <a data-testid="paylink-open-btn" href={link} target="_blank" rel="noreferrer"
                style={{ flex: 1, background: '#E63946', color: '#fff', border: 'none', borderRadius: 12, padding: '13px 18px', fontWeight: 800, textAlign: 'center', textDecoration: 'none', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6, fontFamily: 'Oswald, sans-serif', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <ExternalLink className="h-4 w-4" /> Open
              </a>
            </div>
            <p style={{ fontSize: 11, color: '#555', marginTop: 12, lineHeight: 1.5 }}>
              Share this link with the customer. Once they pay, the booking / registration will auto-confirm.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
